import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import type { CanvasCapture } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { canvasKeys, getSession } from '../api/canvas-captures';
import { ExtensionBrowse } from '../extension/extension-browse';
import { AlertIcon, ArrowLeftIcon, CheckIcon } from '../lib/icons';
import { logger } from '../lib/logger';
import { useIsMobile } from '../lib/use-is-mobile';
import {
  ArrivalRoom,
  type WalkProblem,
  type WalkStage,
} from '../research-room/room-arrival';
import { RoomIntroModal } from '../research-room/room-intro-modal';
import { useResearchRoomStore } from '../store/research-room-store';
import { CanvasBoard } from './canvas-board';
import { DesktopNotice } from './mobile-session';
import { SessionEndedView, SessionFailedView } from './session-status-view';
import { hostOf, isLocalTarget, normalizeTargetUrl } from './target-url';
import { type CapturedShot, useBrowserSession } from './use-browser-session';
import { FLIGHT_MS, OPENING_ZOOM_MAX } from './use-canvas-viewport';
import { useCaptures } from './use-captures';
import { useInstantRun, type WalkedPage } from './use-instant-run';
import { useSession } from './use-session';
import { LiveScreen } from './walk-board';

const log = logger.child({ module: 'browse' });

/**
 * "/browse?url=…" — a real browser instance walking the user's product.
 *
 * It opens the page, waits for it, scrolls through it a screen at a time,
 * stitches the screens into one full-page capture on a new session's board,
 * then opens that board. Nobody drives, and all of it happens on the board
 * itself: the live browser is a card where the capture will land (see
 * walk-board.tsx). `?project=` picks the project the session goes in; without it the
 * server makes one named after the URL's host.
 *
 * The target is a query param rather than component state so the screen has all
 * the properties a screen should: refresh reconnects to the same site, the back
 * button returns to import, and a session is linkable. `BrowseSession` is keyed
 * on it, so pasting a different URL into the address bar of the *browser* app
 * (not the page's own address bar) rebuilds the session cleanly instead of
 * leaving a worker pointed at the old site.
 */
export function BrowsePage() {
  const [params, setParams] = useSearchParams();
  const target = normalizeTargetUrl(params.get('url') ?? '');
  const sessionId = params.get('session');
  const projectId = params.get('project');
  /**
   * Arrived on a session that already exists — a reload mid-walk, or an old
   * link. Only a walk cares: the extension's screen rejoins its session by
   * design.
   *
   * Read once, on arrival, and never again: the walk's own first capture puts
   * `?session=` into the URL while its screens are still uploading, and seeing
   * that here would send the user to the board mid-walk.
   */
  const [rejoined] = useState(
    () => Boolean(sessionId),
    // Read on mount only — see above.
  );
  /** Sent by the Chrome extension (`/auth/extension`). Read on arrival: the
   *  session id landing in the URL replaces the history entry, state and all. */
  const location = useLocation();
  const [fromExtension] = useState(
    () => (location.state as { from?: string } | null)?.from === 'extension',
  );
  /** What a room is, said the moment they land — the walk and the panel set
   *  themselves up behind it, so reading it costs no wait. Read by the
   *  handover as a ref: the board only carries it on if it is still open. */
  const [introOpen, setIntroOpen] = useState(fromExtension);
  const introOpenRef = useRef(introOpen);
  useEffect(() => {
    introOpenRef.current = introOpen;
  }, [introOpen]);
  const closeIntro = useCallback(() => {
    setIntroOpen(false);
  }, []);

  // Starting the session and putting its id in the URL happens here, above the
  // browser, so a reload rejoins the same session rather than splitting one
  // walk's captures across two.
  const {
    ensureSession,
    error,
    sessionId: activeSessionId,
  } = useSession(target ?? '', sessionId, projectId);

  /**
   * Set the moment the walk decides to leave for its board.
   *
   * Without it the two navigations collide and the wrong one wins: the session is
   * created at the *start* of saving a capture, so the effect below is queued
   * while the PNG is still uploading, and its `replace` lands after the
   * walk's — putting the user back on /browse with a screenshot that
   * had already opened its board for a moment.
   *
   * A ref rather than state because it must be readable by an effect in the
   * same commit that sets it; re-rendering to record "we are leaving" is a
   * render nobody would ever see.
   */
  const leavingRef = useRef(false);

  // Reflect the session into the URL once one exists — which is when the first
  // capture creates it. Replace, not push, so Back returns where the user came
  // from rather than to this screen mid-walk.
  useEffect(() => {
    if (leavingRef.current) return;
    if (!activeSessionId || sessionId === activeSessionId) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('session', activeSessionId);
        return next;
      },
      { replace: true },
    );
  }, [activeSessionId, sessionId, setParams]);

  if (!target) return <InvalidTarget raw={params.get('url')} />;
  if (error) return <SessionStartFailed />;

  const intro = introOpen ? <RoomIntroModal onClose={closeIntro} /> : null;

  // Same screen, no worker: the instance runs in Cloud Run and cannot reach the
  // user's machine, so the left half is instructions and the shutter is in the
  // Chrome extension. The rail, the session and the board are the same.
  if (isLocalTarget(target)) {
    return (
      <>
        <ExtensionBrowse
          key={target}
          projectId={projectId}
          sessionIdFromUrl={sessionId}
          target={target}
        />
        {intro}
      </>
    );
  }

  // A walk is not resumable, so a session that already exists is shown, not
  // re-walked into a second copy of the same screens.
  if (rejoined && sessionId) {
    return <Navigate replace to={`/sessions/${sessionId}`} />;
  }

  return (
    <>
      <BrowseSession
        sessionId={activeSessionId}
        onLeaveForBoard={() => {
          leavingRef.current = true;
        }}
        // Keyed on the target only: the key must not change when the session id
        // arrives, or the browser would tear down and reconnect the moment the
        // session is created.
        ensureSession={ensureSession}
        fromExtension={fromExtension}
        introOpen={introOpenRef}
        key={target}
        target={target}
      />
      {intro}
    </>
  );
}

/** How far in the board frames the live browser — close enough to watch the
 *  page open, with room around it to read as a card on a board. */
const LIVE_ZOOM_MAX = 0.8;

/** How long the stitched page is given to settle onto the board — its card
 *  growing to full height, the view pulling back — before the board opens. */
const DEVELOP_MS = FLIGHT_MS + 250;

/** The canvas notice fading out before the session opens. */
const NOTICE_FADE_MS = 300;

/** The longest the handover waits for the stored image to be in cache. */
const PRELOAD_MS = 3_000;

/**
 * Fetches and decodes an image, so the board it is about to open on paints it
 * at once rather than blank. Never rejects: a slow image is a worse handover,
 * not a failed one.
 */
function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, PRELOAD_MS);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const image = new Image();
    image.src = src;
    if (typeof image.decode !== 'function') {
      done();
      return;
    }
    image.decode().then(done, done);
  });
}

/** Something an `<img>` can show, from a walked page's image. */
function imageSource(image: Blob | string): string | null {
  if (typeof image === 'string') return `data:image/png;base64,${image}`;
  if (typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(image);
}

interface Developed {
  height: number;
  src: string;
  width: number;
}

function BrowseSession({
  ensureSession,
  fromExtension,
  introOpen,
  onLeaveForBoard,
  sessionId,
  target,
}: {
  ensureSession: () => Promise<string>;
  /** Passed on to the board, which greets the extension's people. */
  fromExtension: boolean;
  /** Whether the intro shown over the walk is still up: the board keeps it
   *  open if so, and does not show it again once it was closed. */
  introOpen: { readonly current: boolean };
  sessionId: string | null;
  /** Called immediately before leaving, so the screen above stops writing the
   *  session id into a URL this browser is about to abandon. */
  onLeaveForBoard: () => void;
  target: string;
}) {
  const navigate = useNavigate();
  const mobile = useIsMobile();

  /**
   * The walk's stored page, learned from its first capture.
   *
   * Held rather than read from `sessionId`, which is still null when the walk
   * starts — the first capture is what creates the session.
   */
  const walkCaptureRef = useRef<CanvasCapture | null>(null);

  const onFirstCapture = useCallback((capture: CanvasCapture) => {
    walkCaptureRef.current = capture;
  }, []);

  /** The stitched page, shown on the card while it uploads. */
  const [developed, setDeveloped] = useState<Developed | null>(null);
  const developedAtRef = useRef(0);

  // Blob URLs hold the whole PNG in memory until revoked.
  useEffect(() => {
    const src = developed?.src;
    if (!src?.startsWith('blob:')) return;
    return () => {
      URL.revokeObjectURL(src);
    };
  }, [developed?.src]);

  /** False once this screen is gone, so a late handover can't pull the user
   *  back from wherever they went instead. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setArrival = useResearchRoomStore((s) => s.setArrival);
  /** The board's notice is fading out; the session is about to open. */
  const [leaving, setLeaving] = useState(false);
  const setRoomOpen = useResearchRoomStore((s) => s.setOpen);
  /** Screens the walk read, for the room's record of it. */
  const screensRef = useRef(0);
  /** For how long the walk took, start to board. */
  const walkStartedAtRef = useRef(Date.now());
  /** Where the hosted browser actually is — set below, once the session is
   *  up. Read at the handover, so a stale closure cannot report the target. */
  const landedRef = useRef(target);

  // The other end of `walk_completed` / `walk_failed` / `walk_abandoned`:
  // without it, someone who closed the tab mid-walk is in no event at all.
  useEffect(() => {
    trackEvent('walk_started');
  }, []);

  /** Leave for the board, once the whole walk is stored and has landed. */
  const onWalkDone = useCallback(() => {
    const walked = walkCaptureRef.current;
    if (!walked) return;
    trackEvent('walk_completed', {
      duration_ms: Date.now() - walkStartedAtRef.current,
      // The page stored is not the one asked for — almost always a sign-in
      // wall, since the hosted browser has none of the user's cookies. A
      // "completed" walk of a login screen is a failure the room inherits.
      redirected: landedElsewhere(target, landedRef.current),
      screens: screensRef.current,
    });
    trackEvent('board_opened', { source: 'instant' });
    // Now, not at the navigation: the effect this silences is queued in the
    // same commit as the capture landing and would otherwise replace it.
    onLeaveForBoard();
    const settle = Math.max(
      0,
      developedAtRef.current + DEVELOP_MS - Date.now(),
    );
    void Promise.all([
      preloadImage(walked.imageUrl),
      new Promise((resolve) => setTimeout(resolve, settle)),
    ])
      .then(() => {
        if (!mountedRef.current) return;
        setLeaving(true);
        return new Promise((resolve) => setTimeout(resolve, NOTICE_FADE_MS));
      })
      .then(() => {
        if (!mountedRef.current) return;
        // The room it opens on carries on from Floto's setup notes — open,
        // whatever the last session left it as: this one has only just begun.
        setRoomOpen(true);
        setArrival({
          host: hostOf(walked.pageUrl),
          screens: screensRef.current,
          sessionId: walked.sessionId,
        });
        // Replace, so Back goes where the user came from rather than to a
        // browser session that has already done its job.
        void navigate(`/sessions/${walked.sessionId}`, {
          replace: true,
          state: {
            fromWalk: true,
            ...(fromExtension
              ? {
                  from: 'extension',
                  ...(introOpen.current ? {} : { intro: false }),
                }
              : {}),
          },
        });
      });
  }, [
    fromExtension,
    introOpen,
    navigate,
    onLeaveForBoard,
    setArrival,
    setRoomOpen,
    target,
  ]);

  // Named the way the session's board names it, once there is a session, so
  // the title does not change at the handover.
  const { data: stored } = useQuery({
    enabled: Boolean(sessionId),
    queryFn: () => getSession(sessionId!),
    queryKey: canvasKeys.session(sessionId ?? 'none'),
    staleTime: Infinity,
  });

  const { captures, saveCaptureAsync } = useCaptures(
    sessionId,
    ensureSession,
    onFirstCapture,
  );

  /**
   * Through a ref, because the cycle is real and not incidental: the session
   * needs `onCaptured` to be built, `onCaptured` needs the walk, and the walk
   * needs the session's `capture`. The ref is the one of those three edges
   * that can be late.
   */
  const receiveRef = useRef<(shot: CapturedShot) => boolean>(() => false);
  const [shots, setShots] = useState(0);

  // Every screenshot is a tile the walk asked for; the walk stitches them and
  // stores the one page.
  const onCaptured = useCallback((shot: CapturedShot) => {
    if (receiveRef.current(shot)) setShots((n) => n + 1);
  }, []);

  /** Show the walked page, store it, and say whether it landed. */
  const onPage = useCallback(
    async (page: WalkedPage) => {
      const src = imageSource(page.image);
      if (src) {
        developedAtRef.current = Date.now();
        setDeveloped({ height: page.height, src, width: page.width });
      }
      try {
        await saveCaptureAsync(page);
        return true;
      } catch (err) {
        log.error({ err }, 'could not store the walked page');
        return false;
      }
    },
    [saveCaptureAsync],
  );

  const session = useBrowserSession({ initialUrl: target, onCaptured });
  const {
    capture,
    capturing,
    endReason,
    failure,
    frame,
    pageError,
    pageStatus,
    quietFor,
    reload,
    retry,
    status,
    stitch,
    url,
    viewport,
  } = session;

  // Wait for the page, walk it a screen at a time, stitch the screens into one.
  // Nobody drives: the user watches their own site open on their board, and
  // the board opens when the page is stored.
  const walk = useInstantRun({
    capture,
    enabled: true,
    onDone: onWalkDone,
    onPage,
    pageStatus,
    quietFor,
    stitch,
  });
  receiveRef.current = walk.receive;
  screensRef.current = walk.screen;
  landedRef.current = url || target;

  // Failures, once each as they happen. The messages themselves can name the
  // site, so only the kind travels.
  useEffect(() => {
    if (pageStatus === 'error') trackEvent('page_load_failed');
  }, [pageStatus]);
  useEffect(() => {
    if (status === 'failed' && failure) {
      trackEvent('browser_session_failed', {
        captures: captures.length,
        reason: failure,
      });
    }
    if (status === 'ended') {
      trackEvent('browser_session_ended', { reason: endReason ?? 'closed' });
    }
    // Only on the transition; the counts ride along as they were then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, failure]);
  const retryBrowser = () => {
    trackEvent('browser_session_retried', { after: status });
    retry();
  };

  if (status === 'failed' && failure) {
    return (
      <SessionFailedView
        captureCount={captures.length}
        kind={failure}
        onRetry={retryBrowser}
      />
    );
  }

  if (status === 'ended') {
    return (
      <SessionEndedView
        captureCount={captures.length}
        onRetry={retryBrowser}
        reason={endReason ?? 'closed'}
      />
    );
  }

  const host = hostOf(url || target);
  const stage: WalkStage =
    status === 'connecting'
      ? 'boot'
      : walk.phase === 'done'
        ? 'open'
        : developed
          ? 'save'
          : walk.phase === 'stitching'
            ? 'stitch'
            : walk.phase === 'walking' || shots > 0
              ? 'walk'
              : 'load';

  const first = captures[0];
  const problem: WalkProblem | null =
    pageStatus === 'error'
      ? {
          body: pageError ?? `${host} didn't respond.`,
          onRetry: () => {
            trackEvent('page_load_retried');
            reload();
          },
          title: "This page didn't load",
        }
      : walk.phase === 'failed'
        ? {
            body: walk.pageError ?? "That page didn't load.",
            exit: first
              ? { label: 'Open the board', to: `/sessions/${first.sessionId}` }
              : { label: 'Back to your rooms', to: '/' },
            title: "We couldn't capture this page",
          }
        : null;

  const loadDetail =
    pageStatus === 'loading'
      ? 'Waiting for the page to respond'
      : walk.phase === 'preparing'
        ? 'Loading the whole page, lazy images and all'
        : 'Letting the first screen settle';

  // A phone gets no board: the room's setup notes, full screen, until the
  // session opens in its phone layout.
  if (mobile) {
    return (
      <div className="flex h-svh flex-col bg-warm-alt">
        <DesktopNotice />
        <ArrivalRoom
          host={host}
          phaseDetail={loadDetail}
          problem={problem}
          screen={walk.screen}
          stage={stage}
        />
      </div>
    );
  }

  return (
    <div className="flex h-svh">
      <CanvasBoard
        captures={[]}
        exitLabel="All rooms"
        onExit={() => {
          trackEvent('walk_abandoned', { stage });
          void navigate('/');
        }}
        onMove={() => undefined}
        onSelect={() => undefined}
        notice={
          stage === 'save' || stage === 'open' ? (
            <span
              className={[
                'flex items-center gap-2 rounded-pill bg-surface px-3.5 py-2 text-ui font-medium text-text-primary shadow-soft ring-1 ring-hairline transition-opacity duration-300 animate-in fade-in slide-in-from-top-1 motion-reduce:animate-none',
                // Gone before the session opens, so it is not cut off by it.
                leaving ? 'opacity-0' : 'opacity-100',
              ].join(' ')}
              role="status"
            >
              {stage === 'save' ? (
                <span className="size-3.5 animate-spin rounded-pill border-2 border-hairline border-t-brand motion-reduce:animate-none" />
              ) : (
                <CheckIcon className="size-3.5 text-success-ink" />
              )}
              {stage === 'save'
                ? 'Saving the page to your room…'
                : 'Saved — opening your room'}
            </span>
          ) : null
        }
        panel={
          <ArrivalRoom
            host={host}
            phaseDetail={loadDetail}
            problem={problem}
            screen={walk.screen}
            stage={stage}
          />
        }
        pending={{
          // Framed close enough to watch while it is live; once stitched, the
          // same fit the session's own board opens with.
          ceiling: developed ? OPENING_ZOOM_MAX : LIVE_ZOOM_MAX,
          content: (
            <LiveScreen
              developed={developed?.src ?? null}
              failed={problem !== null}
              flash={capturing}
              frame={frame}
              host={host}
              pageStatus={status === 'connecting' ? 'loading' : pageStatus}
              stage={stage}
              viewportHeight={viewport.height}
            />
          ),
          height: developed?.height ?? viewport.height,
          key: developed ? 'developed' : 'live',
          busy: !developed,
          label: developed ? 'Capture 1' : `Capturing ${host}…`,
          meta: `${host} · ${String(developed?.width ?? viewport.width)}×${String(developed?.height ?? viewport.height)}`,
          width: developed?.width ?? viewport.width,
        }}
        selectedId={null}
        // No credits up here: they sit bottom-left by the zoom, and this
        // is where the fix list lands once the room opens.
        title={stored?.name ?? stored?.label ?? host}
      />
    </div>
  );
}

/**
 * Whether the browser ended on a different page than it was sent to. Host and
 * path only: a query string or hash that changed on load is the same page, and
 * so is the bare domain's hop to `www.`.
 */
function landedElsewhere(target: string, landed: string): boolean {
  try {
    const a = new URL(target);
    const b = new URL(landed);
    const host = (url: URL) => url.host.replace(/^www\./, '');
    const path = (url: URL) => url.pathname.replace(/\/+$/, '');
    return host(a) !== host(b) || path(a) !== path(b);
  } catch {
    return false;
  }
}

/** The session couldn't be created — without one there is nowhere to put
 *  captures. */
function SessionStartFailed() {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-warm px-6">
      <div className="w-full max-w-[420px]">
        <span className="flex size-11 items-center justify-center rounded-card border border-critical/20 bg-tag-critical text-critical">
          <AlertIcon className="size-5" />
        </span>
        <h1 className="!mt-6 !mb-0 !text-page-title !font-semibold !tracking-tight !text-text-primary">
          Couldn&apos;t open this room
        </h1>
        <p className="mt-2 text-body text-text-secondary">
          The browser would have nowhere to put its captures, so we stopped
          before opening one.
        </p>
        <Link
          className="mt-8 inline-flex h-11 items-center gap-2 rounded-button bg-ink px-5 text-ui-lg font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]"
          to="/"
        >
          <ArrowLeftIcon className="size-[18px] text-highlight" />
          Back to your rooms
        </Link>
      </div>
    </div>
  );
}

/** Someone hand-edited the query string, or a link went stale. */
function InvalidTarget({ raw }: { raw: string | null }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-warm px-6">
      <div className="w-full max-w-[420px]">
        <span className="flex size-11 items-center justify-center rounded-card border border-hairline bg-surface text-text-secondary">
          <AlertIcon className="size-5" />
        </span>
        <h1 className="!mt-6 !mb-0 !text-page-title !font-semibold !tracking-tight !text-text-primary">
          That address won&apos;t open
        </h1>
        <p className="mt-2 text-body text-text-secondary">
          {raw
            ? `"${raw}" isn't a http or https URL we can load in a browser.`
            : 'No URL was given, so there is nothing to open.'}
        </p>
        <Link
          className="mt-8 inline-flex h-11 items-center gap-2 rounded-button bg-ink px-5 text-ui-lg font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]"
          to="/"
        >
          <ArrowLeftIcon className="size-[18px] text-highlight" />
          Back to import
        </Link>
      </div>
    </div>
  );
}
